import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const { setLang, lang } = useApp();

  // Applique la langue enregistrée sur le compte.
  const applyUserLang = (u) => {
    if (u?.lang) setLang(u.lang);
  };

  useEffect(() => {
    // Session en cookie httpOnly : on interroge toujours /auth/me (le cookie part tout seul).
    api.get("/auth/me")
      .then((r) => { setUser(r.data); applyUserLang(r.data); })
      .catch(() => localStorage.removeItem("token"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persiste la préférence de langue sur le compte quand l'utilisateur est connecté
  // (le cookie httpOnly part automatiquement — best-effort, idempotent).
  const userRef = useRef(null);
  useEffect(() => { userRef.current = user; }, [user]);
  useEffect(() => {
    if (userRef.current) api.put("/auth/me/lang", { lang }).catch(() => {});
  }, [lang]);

  // Connexion par IDENTIFIANT + PIN. « remember » garde l'identifiant sur l'appareil.
  // residenceId : résidence choisie via l'annuaire public (multi-résidences).
  const login = async (identifier, pin, remember = true, residenceId = null) => {
    const id = (identifier || "").toLowerCase().trim();
    const body = { identifier: id, pin };
    if (residenceId) body.residence_id = residenceId;
    const { data } = await api.post("/auth/login", body);
    localStorage.removeItem("token");   // le jeton vit désormais dans un cookie httpOnly
    if (remember) localStorage.setItem("rememberedIdentifier", id);
    else localStorage.removeItem("rememberedIdentifier");
    setUser(data.user);
    applyUserLang(data.user);
    return data.user;
  };

  // Activation (première connexion) : définit le PIN + complète le profil.
  const activate = async (payload) => {
    const { data } = await api.post("/auth/activate", payload);
    localStorage.removeItem("token");   // cookie httpOnly
    localStorage.setItem("rememberedIdentifier", (payload.identifier || "").toLowerCase().trim());
    setUser(data.user);
    applyUserLang(data.user);
    return data.user;
  };

  const logout = () => {
    api.post("/auth/logout").catch(() => {});   // efface le cookie httpOnly côté serveur
    localStorage.removeItem("token");
    setUser(null);
  };

  // Recharge le profil depuis le serveur (ex : après un changement de PIN obligatoire).
  const refreshUser = async () => {
    const { data } = await api.get("/auth/me");
    setUser(data);
    applyUserLang(data);
    return data;
  };

  // Change le code PIN. Le PIN actuel est requis SAUF en changement obligatoire (must_change_pin).
  const changePin = async (newPin, currentPin = null) => {
    const body = { new_pin: newPin };
    if (currentPin) body.current_pin = currentPin;
    await api.post("/auth/change-pin", body);
    await refreshUser();
  };

  const isRole = (...roles) => user && roles.includes(user.role);

  // Valeur mémoïsée : évite de re-rendre tous les consommateurs à chaque rendu du provider.
  const value = useMemo(
    () => ({ user, setUser, loading, login, activate, logout, isRole, changePin, refreshUser }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, loading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
